import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of, throwError } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { environment } from '../../../environments/environment';
import { parseSkillMarkdown } from './skill-md.util';
import { listSkillParameters } from './skill-params.util';
import { slashFromSkillName } from './skill-slash.util';
import type { AgentSkill, AgentSkillListResponse, CreateAgentSkillDto, ImportAgentSkillFile } from './skills.models';

@Injectable({ providedIn: 'root' })
export class SkillsApiService {
  private readonly api = inject(ApiService);
  private readonly changedSubject = new Subject<void>();

  /** Fires after create, update, or delete so the `/` menu can reload. */
  readonly changes = this.changedSubject.asObservable();

  private useMock(): boolean {
    return environment.agenticAiUseMock;
  }

  listSkills(): Observable<AgentSkillListResponse> {
    if (this.useMock()) {
      return of({ items: [] as AgentSkill[] }).pipe(delay(80));
    }
    return this.api.get<{ items?: AgentSkill[] }>('/skills').pipe(
      map((res) => ({ items: Array.isArray(res?.items) ? res.items : [] })),
    );
  }

  createSkill(body: CreateAgentSkillDto): Observable<AgentSkill> {
    if (this.useMock()) {
      const name = String(body.name ?? '').trim();
      const slash = name.startsWith('/') ? name.slice(1).trim().toLowerCase() : name.toLowerCase();
      const skill: AgentSkill = {
        id: `mock-skill-${Date.now()}`,
        scope: 'user',
        slash,
        title: name,
        description: body.description?.trim() || null,
        prompt: String(body.prompt ?? '').trim(),
        parameters: listSkillParameters(String(body.prompt ?? '')),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return of(skill).pipe(delay(150));
    }
    return this.api.post<AgentSkill>('/skills', body).pipe(tap(() => this.changedSubject.next()));
  }

  importSkills(files: ImportAgentSkillFile[]): Observable<{ items: AgentSkill[] }> {
    if (files.length > 5) {
      return throwError(() => ({ error: { message: 'You can upload at most 5 markdown files.' } }));
    }
    if (this.useMock()) {
      const items: AgentSkill[] = [];
      for (const file of files) {
        const parsed = parseSkillMarkdown(file.markdown);
        if (!parsed.ok) {
          return throwError(() => ({ error: { message: `${file.fileName}: ${parsed.error}` } }));
        }
        const slash = slashFromSkillName(parsed.skill.name);
        items.push({
          id: `mock-skill-${Date.now()}-${items.length}`,
          scope: 'user',
          slash,
          title: parsed.skill.name,
          description: parsed.skill.description,
          prompt: parsed.skill.prompt,
          parameters: listSkillParameters(parsed.skill.prompt),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      return of({ items }).pipe(
        delay(150),
        tap(() => this.changedSubject.next()),
      );
    }
    return this.api
      .post<{ items?: AgentSkill[] }>('/skills/import', { files })
      .pipe(
        map((res) => ({ items: Array.isArray(res?.items) ? res.items : [] })),
        tap(() => this.changedSubject.next()),
      );
  }

  updateSkill(id: string, body: CreateAgentSkillDto): Observable<AgentSkill> {
    if (this.useMock()) {
      const name = String(body.name ?? '').trim();
      const slash = name.startsWith('/') ? name.slice(1).trim().toLowerCase() : name.toLowerCase();
      const skill: AgentSkill = {
        id,
        scope: 'user',
        slash,
        title: name,
        description: body.description?.trim() || null,
        prompt: String(body.prompt ?? '').trim(),
        parameters: listSkillParameters(String(body.prompt ?? '')),
        updatedAt: new Date().toISOString(),
      };
      return of(skill).pipe(
        delay(150),
        tap(() => this.changedSubject.next()),
      );
    }
    return this.api.patch<AgentSkill>(`/skills/${id}`, body).pipe(tap(() => this.changedSubject.next()));
  }

  deleteSkill(id: string): Observable<{ deleted: boolean }> {
    if (this.useMock()) {
      return of({ deleted: true }).pipe(
        delay(80),
        tap(() => this.changedSubject.next()),
      );
    }
    return this.api.delete<{ deleted: boolean }>(`/skills/${id}`).pipe(tap(() => this.changedSubject.next()));
  }
}
